import { useState, useEffect } from "react";
import { Heart, UtensilsCrossed, Lock, Ban, CalendarClock, Timer, IceCreamCone, Hourglass, CircleCheck } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { Confetti } from "@/components/Confetti";
import { SlideToReveal } from "@/components/SlideToReveal";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useLang } from "@/lib/i18n";
import { formatApiErrorDetail } from "@/lib/api";

const ACTIVE_MS = 30 * 60 * 1000;

function useCountdown(aktivert) {
  const [left, setLeft] = useState(() => (aktivert ? new Date(aktivert).getTime() + ACTIVE_MS - Date.now() : null));
  useEffect(() => {
    if (!aktivert) return;
    const tick = () => setLeft(new Date(aktivert).getTime() + ACTIVE_MS - Date.now());
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [aktivert]);
  return left;
}

function fmt(ms) {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}

function fmtDate(iso, locale) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(locale, { day: "2-digit", month: "long", year: "numeric", timeZone: "Europe/Oslo" });
  } catch {
    return null;
  }
}

function fmtDateTime(iso, locale) {
  return new Date(iso).toLocaleString(locale, { day: "2-digit", month: "long", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Oslo" });
}

function useNow(intervalMs) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

export function CouponReveal({ tittel, fornavn, utloper, gyldigFra, aktivert: initialAktivert, innlost: initialInnlost, codeBrukt, note, heading, subheading, onActivate, onRedeem, footer }) {
  const { t, locale } = useLang();
  const [aktivert, setAktivert] = useState(initialAktivert || null);
  const [innlost, setInnlost] = useState(initialInnlost || null);
  const [redeemKey, setRedeemKey] = useState(0);
  const [showConfetti, setShowConfetti] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sliderKey, setSliderKey] = useState(0);
  const [activating, setActivating] = useState(false);
  const left = useCountdown(aktivert);
  const revealed = !!aktivert && left > 0 && !innlost;
  const alreadyUsed = !!innlost || ((codeBrukt || !!aktivert) && !revealed);
  const utloperText = fmtDate(utloper, locale);
  const now = useNow(30000);
  const locked = !!gyldigFra && new Date(gyldigFra).getTime() > now;
  const gyldigFraText = fmtDate(gyldigFra, locale);

  const handleConfirm = async () => {
    setActivating(true);
    try {
      const res = await onActivate();
      setConfirmOpen(false);
      setAktivert(res.aktivert);
      if (!res.allerede_aktivert) {
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 4500);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail ? formatApiErrorDetail(err.response.data.detail) : t("Kunne ikke aktivere kupongen", "Could not activate the coupon"));
      setConfirmOpen(false);
      setSliderKey((k) => k + 1);
    } finally {
      setActivating(false);
    }
  };

  const handleRedeem = async () => {
    try {
      const res = await onRedeem();
      setInnlost(res.innlost);
      toast.success(t("Kupongen er innløst", "The coupon has been redeemed"));
    } catch (err) {
      toast.error(err.response?.data?.detail ? formatApiErrorDetail(err.response.data.detail) : t("Kunne ikke bekrefte innløsning", "Could not confirm redemption"));
      setRedeemKey((k) => k + 1);
    }
  };

  const handleCancel = () => {
    setConfirmOpen(false);
    setSliderKey((k) => k + 1);
  };

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-[#1A1715]">
      {showConfetti && <Confetti />}
      <div className="pointer-events-none absolute -top-40 left-1/2 h-96 w-96 -translate-x-1/2 rounded-full bg-[#D4AF37]/10 blur-3xl" />

      <AlertDialog open={confirmOpen} onOpenChange={(o) => !o && !activating && handleCancel()}>
        <AlertDialogContent className="border-[#D4AF37]/30 bg-[#282421] text-[#FDFBF7]" data-testid="confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif-display text-2xl text-[#FDFBF7]">
              {t("Er du sikker på at du vil aktivere kupongen nå?", "Are you sure you want to activate the coupon now?")}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#A8A29E]">
              {t(
                "Kupongen er gyldig i 30 minutter etter aktivering og kan bare brukes én gang. Aktiver den først når du er hos Demo og klar til å vise den til servitøren.",
                "The coupon is valid for 30 minutes after activation and can only be used once. Only activate it when you are at Demo and ready to show it to the waiter."
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={handleCancel}
              disabled={activating}
              className="border-white/15 bg-transparent text-[#FDFBF7] hover:bg-white/5 hover:text-[#FDFBF7]"
              data-testid="confirm-cancel-btn"
            >
              {t("Ikke nå", "Not now")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleConfirm();
              }}
              disabled={activating}
              className="bg-[#D4AF37] text-[#1A1715] hover:bg-[#E5C564]"
              data-testid="confirm-activate-btn"
            >
              {activating ? t("Aktiverer…", "Activating…") : t("Ja, aktiver kupongen", "Yes, activate the coupon")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="relative z-10 mx-auto flex min-h-screen max-w-xl flex-col px-5 py-8 sm:py-12">
        <header className="flex items-center justify-center animate-fade-in">
          <Logo />
        </header>

        {alreadyUsed ? (
          <main className="mt-14 flex-1 text-center">
            <div className="animate-fade-up">
              <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full border border-[#A8A29E]/30 bg-white/5">
                {innlost ? <CircleCheck className="h-7 w-7 text-[#D4AF37]" /> : <Ban className="h-7 w-7 text-[#A8A29E]" />}
              </div>
              <h1 className="font-serif-display text-4xl font-600 leading-tight text-[#FDFBF7] sm:text-5xl" data-testid="already-used-message">
                {innlost ? t("Kupongen er innløst", "Coupon redeemed") : t("Kupongen din er brukt", "Your coupon has been used")}
              </h1>
              <p className="mx-auto mt-6 max-w-sm text-base text-[#A8A29E]">
                {innlost
                  ? t(
                      `Takk${fornavn ? `, ${fornavn}` : ""}! Kupongen ble innløst ${fmtDateTime(innlost, locale)}. Velbekomme!`,
                      `Thank you${fornavn ? `, ${fornavn}` : ""}! The coupon was redeemed ${fmtDateTime(innlost, locale)}. Enjoy!`
                    )
                  : t(
                      `${fornavn ? `${fornavn}, d` : "D"}enne kupongen er allerede benyttet hos Demo. Én kupong per gjest.`,
                      `${fornavn ? `${fornavn}, t` : "T"}his coupon has already been used at Demo. One coupon per guest.`
                    )}
              </p>
            </div>
          </main>
        ) : (
          <main className="mt-10 flex-1 text-center">
            <div className="animate-fade-up">
              <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10">
                <Heart className="h-6 w-6 fill-[#D4AF37] text-[#D4AF37]" />
              </div>
              <h1 className="font-serif-display text-4xl font-600 text-[#FDFBF7] sm:text-5xl">{heading}</h1>
              <p className="mt-4 text-lg text-[#FDFBF7]/90">{subheading}</p>
              {note && !revealed && (
                <p className="mx-auto mt-3 max-w-sm rounded-xl border border-[#D4AF37]/25 bg-[#D4AF37]/10 px-4 py-2 text-sm text-[#E5C564]" data-testid="already-registered-note">
                  {note}
                </p>
              )}
            </div>

            <p className="mx-auto mt-8 max-w-sm animate-fade-up text-sm text-[#A8A29E]" style={{ animationDelay: "0.1s" }}>
              {revealed
                ? t("Kupongen er aktiv nå – vis skjermen der du bestiller.", "The coupon is active now – show this screen where you order.")
                : locked
                  ? t("Kupongen gjelder ved ditt neste besøk og låses opp i morgen.", "The coupon is for your next visit and unlocks tomorrow.")
                  : t("Her er kupongen din. Aktiver den når du er hos oss og klar til å nyte den.", "Here is your coupon. Activate it when you are with us and ready to enjoy it.")}
            </p>

            <div className="mx-auto mt-5 animate-scale-in" style={{ animationDelay: "0.2s" }}>
              <div
                className={`relative mx-auto max-w-sm overflow-hidden rounded-2xl border-2 border-dashed px-6 py-8 transition-colors ${
                  revealed ? "animate-gold-pulse border-[#D4AF37]/60 bg-[#282421]" : "border-white/10 bg-[#282421]"
                }`}
                data-testid="coupon-card"
              >
                <IceCreamCone className={`mx-auto mb-3 h-8 w-8 text-[#D4AF37] transition-all duration-500 ${revealed ? "" : "blur-[2px] opacity-60"}`} />
                <p className="mb-2 text-[0.65rem] uppercase tracking-[0.3em] text-[#A8A29E]">
                  {revealed ? t("Aktiv kupong", "Active coupon") : t("Din kupong", "Your coupon")}
                </p>
                <p
                  className={`font-serif-display text-3xl font-700 leading-tight text-[#D4AF37] transition-all duration-500 sm:text-4xl ${
                    revealed ? "blur-0" : "select-none blur-[3px]"
                  }`}
                  data-testid="coupon-text"
                >
                  {tittel}
                </p>
                <p className={`mt-2 text-sm text-[#FDFBF7]/80 transition-all duration-500 ${revealed ? "" : "blur-[3px]"}`}>
                  {t("– med hilsen fra oss i Demo", "– with love from all of us at Demo")}
                </p>
                {revealed && (
                  <div className="mt-5 inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-4 py-1.5 font-mono text-lg text-[#E5C564]" data-testid="coupon-countdown">
                    <Timer className="h-4 w-4" />
                    {fmt(left)}
                  </div>
                )}
                {!revealed && (
                  <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                    <Lock className="h-6 w-6 text-[#A8A29E]/70" />
                  </div>
                )}
              </div>
            </div>

            {revealed && onRedeem && (
              <div className="mx-auto mt-6 w-full max-w-sm animate-fade-up" data-testid="redeem-section">
                <p className="mb-3 text-xs uppercase tracking-[0.2em] text-[#A8A29E]">{t("For servitøren", "For the waiter")}</p>
                <SlideToReveal key={redeemKey} onReveal={handleRedeem} label={t("Sveip for å bekrefte innløsning", "Swipe to confirm redemption")} testId="slide-to-redeem" />
              </div>
            )}
            {!revealed && locked && (
              <div
                className="mx-auto mt-6 flex w-full max-w-sm items-center justify-center gap-2 rounded-full border border-[#D4AF37]/30 bg-[#1A1715] px-5 py-4 text-sm text-[#E5C564] animate-fade-up"
                style={{ animationDelay: "0.3s" }}
                data-testid="coupon-locked"
              >
                <Hourglass className="h-4 w-4" />
                {t(`Kan brukes fra ${gyldigFraText} kl. 00:00`, `Can be used from ${gyldigFraText} at 00:00`)}
              </div>
            )}
            {!revealed && !locked && (
              <p
                className="mx-auto mt-8 max-w-md font-serif-display text-2xl font-600 leading-snug text-[#FDFBF7] sm:text-3xl animate-fade-up"
                style={{ animationDelay: "0.25s" }}
                data-testid="swipe-instruction"
              >
                {t("Sveip foran servitøren eller der du bestiller –", "Swipe in front of the waiter or where you order –")}{" "}
                <span className="text-[#D4AF37]">{t("kupongen er klar til bruk.", "the coupon is ready to use.")}</span>
              </p>
            )}
            {!revealed && !locked && (
              <div className="mx-auto mt-5 w-full max-w-sm animate-fade-up" style={{ animationDelay: "0.3s" }}>
                <SlideToReveal key={sliderKey} onReveal={() => setConfirmOpen(true)} />
              </div>
            )}

            <div className="mx-auto mt-8 max-w-sm animate-fade-up space-y-3 text-center" style={{ animationDelay: "0.4s" }}>
              {utloperText && !revealed && (
                <div className="flex items-center justify-center gap-2 text-sm font-medium text-[#E5C564]" data-testid="code-expiry">
                  <CalendarClock className="h-4 w-4" />
                  {t("Må aktiveres innen", "Must be activated by")} {utloperText}
                </div>
              )}
              <div className="flex items-center justify-center gap-2 text-sm text-[#FDFBF7]">
                <UtensilsCrossed className="h-4 w-4 text-[#D4AF37]" />
                {revealed
                  ? t("Kupongen forsvinner automatisk når tiden er ute.", "The coupon disappears automatically when time runs out.")
                  : locked
                    ? t("Ta med deg telefonen ved neste besøk – da kan du sveipe foran servitøren eller der du bestiller.", "Bring your phone on your next visit – then you can swipe in front of the waiter or where you order.")
                    : t("Når du er hos Demo, kan du sveipe foran servitøren eller der du bestiller.", "When you are at Demo, swipe in front of the waiter or where you order.")}
              </div>
              <p className="text-sm font-medium text-[#E5C564]" data-testid="one-coupon-at-a-time">{t("Du kan kun bruke én kupong om gangen.", "You can only use one coupon at a time.")}</p>
              <p className="text-xs text-[#A8A29E]">{t("Kupongen er gyldig i 30 minutter etter aktivering.", "The coupon is valid for 30 minutes after activation.")}</p>
            </div>
          </main>
        )}

        {footer && <footer className="mt-10 text-center text-xs text-[#A8A29E]/60">{footer}</footer>}
      </div>
    </div>
  );
}
